import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { PiArrowBendUpLeftLight, PiThumbsUpLight, PiCheckLight, PiChecksLight } from 'react-icons/pi';
import { motion, AnimatePresence } from 'framer-motion';
import ProfileImage from '../Header/ProfileImage';
import { Tooltip } from '@mui/material';
import { formatDateChat, formatTime } from '../../utils/functions';
import { normalise } from '../shared/access/accessModel';
import Spinner from '../shared/TailwindComponents/Spinner';

interface NewMessageListProps {
  messages: any[];
  selectedChat: any;
  isLoading: boolean;
  loadMoreMessages: () => void;
  hasMore: boolean;
  page: number;
  isTyping: boolean;
  userMap: Record<string, any>;

  // Keep prop for future, but we do NOT spam it anymore.
  onMessagesRead: (messageIds: string[]) => void;

  onReply: (messageId: string) => void;
}

const SkeletonMessage = () => (
  <div className="flex my-2 justify-end">
    <div className="bg-gray-200 dark:bg-gray-700 rounded-lg p-4 w-3/4 border border-gray-300 dark:border-gray-800">
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2 animate-fadePulse"></div>
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 animate-fadePulse"></div>
    </div>
  </div>
);

interface MessageProps {
  msg: any;
  myUserId: string;
  shouldAnimate: boolean;
  senderInfo?: any;
  isGroup: boolean;
  showName: boolean;
  showTimestamp: boolean;
  onReply: (messageId: string) => void;
  messageRefs: Record<string, React.RefObject<HTMLDivElement | null>>;
}

const Message: React.FC<MessageProps> = React.memo(
  ({ msg, myUserId, shouldAnimate, senderInfo, isGroup, showName, showTimestamp, onReply, messageRefs }) => {
    const { t } = useTranslation();
    const [isHovered, setIsHovered] = useState(false);

    const sender = String(msg.sender || msg.SenderID || msg.sender_id || '');
    const isSender = sender === myUserId;

    const contentToRender = typeof msg.content === 'string' ? msg.content : '';
    const attachments = Array.isArray(msg.attachments) ? msg.attachments : [];
    const hasAttachments = attachments.length > 0;

    const stableKey = String(msg.local_message_id || msg.id || msg.MessageID);

    return (
      <motion.div
        // ref={messageRefs[stableKey]}
        initial={shouldAnimate ? { opacity: 0, y: 10 } : false}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`flex my-2 ${isSender ? 'justify-end' : 'justify-start'}`}
      >
        <div className="flex gap-2 max-w-xs lg:max-w-md">
          {!isSender && senderInfo && (
            <div className="flex-shrink-0">
              <ProfileImage
                profilePicture={normalise(senderInfo).avatar}
                email={senderInfo.Email}
                initials={senderInfo.Initials || '?'}
                name={senderInfo.Name}
                firstName={senderInfo.FirstName}
                lastName={senderInfo.LastName}
                size="h-8 w-8"
              />
            </div>
          )}

          <div className="flex flex-col gap-1">
            {showName && !isSender && (
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400 px-3">
                {senderInfo?.Name || t('Unknown')}
              </p>
            )}

            <div
              className={`rounded-lg px-4 py-2 ${
                isSender
                  ? 'bg-primary-500 text-white rounded-br-none'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white rounded-bl-none'
              } ${msg.status === 'sending' ? 'opacity-60' : ''}`}
            >
              {hasAttachments ? (
                <div className="flex flex-col gap-2">
                  {contentToRender ? <p className="text-sm break-words whitespace-pre-wrap">{contentToRender}</p> : null}

                  <div className="flex flex-col gap-2">
                    {attachments.map((a: any, idx: number) => {
                      const url = a?.url;
                      if (!url) return null;

                      const name = a?.name || url;
                      const mime = String(a?.mimeType || '').toLowerCase();
                      const isImage = a?.kind === 'image' || mime.startsWith('image/');

                      return (
                        <div key={`${url}-${idx}`} className="flex flex-col gap-1">
                          {isImage ? (
                            <a href={url} target="_blank" rel="noopener noreferrer">
                              <img
                                src={a.thumbUrl || url}
                                alt={name}
                                className="max-w-[220px] rounded-md border border-white/10"
                                loading="lazy"
                              />
                            </a>
                          ) : (
                            <a href={url} target="_blank" rel="noopener noreferrer" className="underline text-sm">
                              📎 {name}
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <p className="text-sm break-words whitespace-pre-wrap">{contentToRender}</p>
              )}
            </div>

            <div className="flex items-center gap-1">
              {showTimestamp && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {formatTime(msg.sentAt || msg.timestamp || '')}
                </p>
              )}

              {isSender && (
                <div className="flex items-center">
                  {msg.status === 'sending' && (
                    <Spinner size="glyph" className="!h-4 !w-4" />
                  )}
                  {msg.status === 'sent' && (
                    <Tooltip title={t("Sent")}>
                      <PiCheckLight className="text-sm text-gray-400" />
                    </Tooltip>
                  )}
                  {msg.status === 'delivered' && (
                    <Tooltip title={t("Delivered")}>
                      <PiChecksLight className="text-sm text-primary-500" />
                    </Tooltip>
                  )}
                </div>
              )}
            </div>

            {msg.status === 'failed' && <p className="text-xs text-danger-500">{t('Failed to send')}</p>}
          </div>

          {isHovered && (
            <div className="flex gap-1">
              <Tooltip title={t("Reply")}>
                <button
                  onClick={() => onReply(String(msg.id || msg.MessageID || stableKey))}
                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                >
                  <PiArrowBendUpLeftLight className="text-lg text-gray-600 dark:text-gray-400" />
                </button>
              </Tooltip>
              <Tooltip title={t('React')}>
                <button className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors">
                  <PiThumbsUpLight className="text-lg text-gray-600 dark:text-gray-400" />
                </button>
              </Tooltip>
            </div>
          )}
        </div>
      </motion.div>
    );
  }
);

Message.displayName = 'Message';

const NewMessageList: React.FC<NewMessageListProps> = ({
  messages,
  selectedChat,
  isLoading,
  loadMoreMessages,
  hasMore,
  page,
  isTyping,
  userMap,
  onReply,
}) => {
  const { t } = useTranslation();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const myUserId = userInfo?.UserID ? String(userInfo.UserID) : '';

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messageRefs = useRef<Record<string, React.RefObject<HTMLDivElement | null>>>({});

  const isGroup = selectedChat?.isGroup || false;

  // Ensure refs exist for stable keys
  messages.forEach(msg => {
    const key = String(msg.local_message_id || msg.id || msg.MessageID);
    if (!messageRefs.current[key]) messageRefs.current[key] = React.createRef();
  });

  const groupedMessages = useMemo(() => {
    const groups: Record<string, any[]> = {};
    messages.forEach(msg => {
      const date = formatDateChat(msg.sentAt || msg.timestamp || '');
      if (!groups[date]) groups[date] = [];
      groups[date].push(msg);
    });
    return groups;
  }, [messages]);

  return (
    <div className="flex-1 overflow-y-auto flex flex-col bg-white dark:bg-gray-900 p-4">
      {isLoading && (
        <div className="space-y-2">
          <SkeletonMessage />
          <SkeletonMessage />
          <SkeletonMessage />
        </div>
      )}

      <AnimatePresence>
        {messages.length === 0 && !isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex-1 flex items-center justify-center"
          >
            <p className="text-gray-500 dark:text-gray-400">{t('No messages yet. Start the conversation!')}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* NOTE:
         We are NOT auto-firing message_read here anymore.
         Read receipts are handled centrally in NewMessageCenter.
      */}

      {Object.entries(groupedMessages).map(([date, dateMessages]) => (
        <div key={date}>
          <div className="flex items-center justify-center my-4">
            <p className="text-xs text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-900 px-3">{date}</p>
          </div>

          {(dateMessages as any[]).map((msg: any, index: number) => {
            const prevMsg = index > 0 ? (dateMessages as any[])[index - 1] : null;
            const nextMsg = index < (dateMessages as any[]).length - 1 ? (dateMessages as any[])[index + 1] : null;

            const sender = String(msg.sender || msg.SenderID || msg.sender_id || '');
            const prevSender = prevMsg ? String(prevMsg.sender || prevMsg.SenderID || prevMsg.sender_id || '') : null;
            const nextSender = nextMsg ? String(nextMsg.sender || nextMsg.SenderID || nextMsg.sender_id || '') : null;

            const senderInfo = userMap[sender];
            const showName = isGroup && sender !== myUserId && (!prevMsg || prevSender !== sender);
            const showTimestamp = !nextMsg || nextSender !== sender;

            const stableKey = String(msg.local_message_id || msg.id || msg.MessageID);

            return (
              <Message
                key={stableKey}
                msg={msg}
                myUserId={myUserId}
                shouldAnimate={msg.status === 'sending'}
                senderInfo={senderInfo}
                isGroup={isGroup}
                showName={showName}
                showTimestamp={showTimestamp}
                onReply={onReply}
                messageRefs={messageRefs.current}
              />
            );
          })}
        </div>
      ))}

      {isTyping && (
        <div className="flex my-2 gap-2">
          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
        </div>
      )}

      <div ref={messagesEndRef} />
    </div>
  );
};

export default NewMessageList;
